<template>
  <div class="authBox">
    <div class="authBox-background">
      <div class="authBox-background-container">
        <div class="authBox-background-container-title">
          <left-square-outlined @click="backup" />
          <span class="titleText">{{ $t('AccountInfo.AuthenUser.personalAuth') }}</span>
          <a-divider></a-divider>
        </div>
        <div class="authBox-background-container-body">
          <a-form ref="formRef"
                  :model="formState"
                  :rules="formRules"
                  :label-col="labelCol"
                  :wrapper-col="wrapperCol">
            <a-form-item :label="$t('AccountInfo.AuthenUser.realName')"
                         name="name"
                         required>
              <a-input v-model:value="formState.name"
                       :placeholder="$t('AccountInfo.AuthenUser.realNamePlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenUser.idType')">
              <a-radio-group v-model:value="formState.type"
                             style="float:left">
                <a-radio :value="1">{{ $t('AccountInfo.AuthenUser.idCard') }}</a-radio>
              </a-radio-group>
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenUser.idNumber')"
                         name="id_number"
                         required>
              <a-input v-model:value="formState.id_number"
                       :placeholder="$t('AccountInfo.AuthenUser.idNumberPlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenUser.idPhoto')"
                         name="id_front"
                         required>
              <div class="uploadStyle">
                <a-upload v-model:file-list="fileList"
                          name="avatar"
                          class="uploader"
                          list-type="picture-card"
                          :show-upload-list="false"
                          :custom-request="(fileItem: FileInfo) => customUpload(fileItem, 'front')"
                          :before-upload="beforeUpload"
                          @change="(info: FileInfo) => handleChange(info, 'front')">
                  <a-image v-if="formState.id_front"
                           :width="'100%'"
                           :height="'100%'"
                           :preview="false"
                           :src="toUrl(formState.id_front)" />
                  <div v-else>
                    <loading-outlined v-if="frontLoading"></loading-outlined>
                    <plus-outlined v-else></plus-outlined>
                    <div class="ant-upload-text">{{ $t('AccountInfo.AuthenUser.idFrontPhoto') }}</div>
                  </div>
                </a-upload>
                <a-upload v-model:file-list="fileList"
                          name="avatar"
                          class="uploader"
                          list-type="picture-card"
                          :show-upload-list="false"
                          :custom-request="(fileItem: FileInfo) => customUpload(fileItem, 'back')"
                          :before-upload="beforeUpload"
                          @change="(info: FileInfo) => handleChange(info, 'back')">
                  <a-image v-if="formState.id_back"
                           :width="'100%'"
                           :height="'100%'"
                           :preview="false"
                           :src="toUrl(formState.id_back)" />
                  <div v-else>
                    <loading-outlined v-if="backLoading"></loading-outlined>
                    <plus-outlined v-else></plus-outlined>
                    <div class="ant-upload-text">{{ $t('AccountInfo.AuthenUser.idBackPhoto') }}</div>
                  </div>
                </a-upload>
                <div class="uploadStyle-text">
                  <span>{{ $t('AccountInfo.AuthenUser.uploadInstructions') }}</span>
                  <span>{{ $t('AccountInfo.AuthenUser.uploadInstruction1') }}</span>
                  <span>{{ $t('AccountInfo.AuthenUser.uploadInstruction2') }}</span>
                  <span>{{ $t('AccountInfo.AuthenUser.uploadInstruction3') }}</span>
                </div>
              </div>
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenUser.contactName')">
              <a-input v-model:value="formState.contact_name"
                       :placeholder="$t('AccountInfo.AuthenUser.contactNamePlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenUser.contactPhone')">
              <a-input v-model:value="formState.contact_phone"
                       :placeholder="$t('AccountInfo.AuthenUser.contactPhonePlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenUser.contactEmail')">
              <a-input v-model:value="formState.contact_email"
                       :placeholder="$t('AccountInfo.AuthenUser.contactEmailPlaceholder')" />
            </a-form-item>
            <a-form-item :label="$t('AccountInfo.AuthenUser.hasShop')"
                         name="has_shop"
                         required>
              <a-radio-group v-model:value="formState.has_shop"
                             style="float:left">
                <a-radio :value='true'>{{ $t('AccountInfo.AuthenUser.yes') }}</a-radio>
                <a-radio :value='false'>{{ $t('AccountInfo.AuthenUser.no') }}</a-radio>
              </a-radio-group>
            </a-form-item>
            <a-form-item v-if="formState.has_shop == true"
                         :label="$t('AccountInfo.AuthenUser.shopAddress')"
                         name="shop">
              <a-input v-model:value="formState.shop"
                       :placeholder="$t('AccountInfo.AuthenUser.shopAddressPlaceholder')" />
            </a-form-item>
            <a-form-item :wrapper-col="{ span: 10, offset: 5 }">
              <a-button type="primary"
                        class="footerSubmit"
                        @click="onSubmit">{{ $t('AccountInfo.AuthenUser.submit') }}</a-button>
            </a-form-item>
          </a-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CORE_ROUTER } from '@/core'
import { toUrl } from '@/utils'
import { message } from 'ant-design-vue'
import { container } from 'tsyringe'
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Router } from 'vue-router'
import { authUserEdit, authUserRegister, authUserState, uploadImage } from '../api/Auth'
import type { FileInfo, FileItem, UserRegisterState } from '../api/interface/AuthInterface'

onMounted(() => {
  authUserState().then(result => {
    const { status } = result as UserRegisterState
    if (status == 'failed') {
      formType.value = true
      for (const resultKey in formState) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formState as any)[resultKey] = (result as any)[resultKey]
      }
    }
  })
})

// declare State
const router = container.resolve<Router>(CORE_ROUTER)
const formRef = ref()
const formType = ref<boolean>(false)
const { t } = useI18n()
const labelCol = { span: 8 }
const wrapperCol = { span: 12 }
const formState = reactive<UserRegisterState>({
  name: '',
  type: 1,
  id_number: '',
  id_front: '',
  id_back: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  has_shop: false,
  shop: '',
})
const formRules = computed(() => ({
  name: [{ type: 'string', required: true, message: t('AccountInfo.AuthenUser.realNameError'), trigger: 'blur' }],
  id_number: [{ type: 'string', required: true, trigger: 'blur' }, {
    validator(rule: string, value: string, callback: (...arg: unknown[]) => unknown) {
      if (!value) {
        callback()
      } else {
        if (!/(^\d{18}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)) {
          callback(new Error(t('AccountInfo.AuthenUser.idNumberError')))
        } else {
          callback()
        }
      }
    }
  }],
  id_front: [{ type: 'string', required: true, message: t('AccountInfo.AuthenUser.idPhotoError'), trigger: 'blur' }],
  id_back: [{ type: 'string', required: true, message: t('AccountInfo.AuthenUser.idPhotoError'), trigger: 'blur' }],
  shop: [{ type: 'url', required: true, message: t('AccountInfo.AuthenUser.shopAddressError'), trigger: 'blur' }],
}))
const fileList = ref([])
const frontLoading = ref<boolean>(false)
const backLoading = ref<boolean>(false)

// declare Method
const backup = () => {
  router.go(-1)
}
const handleChange = (info: FileInfo, state: 'front' | 'back') => {
  if (info.file.status === 'uploading') {
    if (state == 'front')
      frontLoading.value = true
    else backLoading.value = true
    return
  }
  if (info.file.status === 'done') {
    if (state == 'front')
      frontLoading.value = false
    else backLoading.value = false
  }
  if (info.file.status === 'error') {
    if (state == 'front')
      frontLoading.value = false
    else backLoading.value = false
    message.error(t('AccountInfo.AuthenUser.imageUploadError'))
  }
}
const beforeUpload = (file: FileItem) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
  if (!isJpgOrPng) {
    message.error(t('AccountInfo.AuthenUser.imageFormatError'))
  }
  const isLt2M = file.size / 1024 / 1024 < 2
  if (!isLt2M) {
    message.error(t('AccountInfo.AuthenUser.imageSizeError'))
  }
  return isJpgOrPng && isLt2M
}
const customUpload = (fileItem: { file: FileItem }, state: 'front' | 'back') => {
  const { file } = fileItem
  uploadImage(file).then(result => {
    if (result.uploaded) {
      const { uploaded } = result
      if (state == 'front')
        formState.id_front = uploaded
      else formState.id_back = uploaded
      return true
    }
  })
}
const onSubmit = (): void => {
  if (formState.has_shop == false) {
    formState.shop = ''
  }
  formRef.value.validate().then(() => {
    if (!formType.value) {
      authUserRegister(formState).then(() => {
        message.success(t('AccountInfo.AuthenUser.submitSuccess'))
        backup()
      })
    } else {
      authUserEdit(formState).then(() => {
        message.success(t('AccountInfo.AuthenUser.submitSuccess'))
        backup()
      })
    }
  }).catch(() => {
    //
  })
}
</script>


<style lang="less" scoped>
.authBox {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: rgba(246, 241, 241, 0.24);

  &-background {
    width: 97%;
    height: 95%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-container {
      width: 95%;
      justify-content: center;
      text-align: center;
      flex-direction: column;
      overflow: auto;

      &-title {
        text-align: left;
        padding-top: 20px;

        span:first-child {
          font-size: 19px;
        }

        .titleText {
          font-size: 19px;
          padding-left: 13px;
          font-family: PingFangSC-Medium, PingFang SC;
          font-weight: 500;
          color: rgba(0, 0, 0, 0.8500);
          line-height: 24px;
        }
      }

      &-body {
        display: flex;
        justify-content: center;
        align-items: center;

        form {
          width: 80%;

          .uploadStyle {
            display: flex;

            .uploader {
              text-align: left;
              flex-basis: 30%;

              :deep(.ant-upload) {
                width: 21vh;
                height: 15vh;
              }
            }

            &-text {
              display: flex;
              flex-direction: column;
              font-size: 12px;
              font-family: PingFangSC-Regular, PingFang SC;
              font-weight: 400;
              color: rgba(0, 0, 0, 0.4500);
              line-height: 20px;

              span {
                text-align: left;
                white-space: nowrap;
              }
            }
          }

          .footerSubmit {
            background: #002FA7
          }
        }
      }
    }
  }
}
</style>
